import { StackScreenProps } from "@react-navigation/stack";
import { ViewStockOrderRootScreenParamList } from "../../../../types";
import { useTranslation } from "../../../../utilities/i18n";
import { useStockOrderContextProvider } from "../../utils/active-stock-order";
import { ConfirmationPrompt } from "../../../../components/ConfirmationPrompt";
import {
  Badge,
  Card,
  FlatActionList,
  Page,
  Stack,
  TileButtonActionList,
  useFlashMessages,
  Text,
} from "@towersystems/roam-common-native";
import { Spacings, View } from "react-native-ui-lib";
import React from "react";
import {
  MUTATION_DELETE_STOCK_ORDER,
  SEND_STOCK_ORDER_INBOUND_RETAILER,
} from "../../utils";
import {
  DeletionResponse,
  DeletionResult,
  RetailerInboundResourceType,
  Success,
} from "@towersystems/roam-common/lib/generated-types";
import { useMutation } from "@apollo/client";
import { OrderItemFlatActionList } from "../../components/OrderItemFlatActionList/OrderItemFlatActionList";
import { HTML5_FMT } from "@towersystems/roam-common/lib/shared-utils";
import { formatDate } from "../../../../utilities/date/format-date";
import { useGetFormattedInboundRetailerDataForResource } from "../../../../utilities/retailer-inbound";
import { useDownloadJson } from "../../../../utilities/download-json";
import { Spacer } from "../../../../components/Spacer";

export type ViewStockOrderScreenProps = StackScreenProps<
  ViewStockOrderRootScreenParamList,
  "ViewStockOrderScreen"
>;

export const ViewStockOrderScreen = ({
  navigation,
}: ViewStockOrderScreenProps) => {
  const { t } = useTranslation("stockorder");
  const { stockOrder } = useStockOrderContextProvider();
  const [promptVisible, setPromptVisible] = React.useState(false);
  const { download } = useDownloadJson();

  const { loading: loadingResourceData, getFormattedResourceData } =
    useGetFormattedInboundRetailerDataForResource();

  const handleGetFormattedResourceData = React.useCallback(
    async (stockOrderId: string) => {
      return getFormattedResourceData(
        RetailerInboundResourceType.STOCK_ORDER,
        stockOrderId
      )
        .then((result) => {
          return download({
            data: result as any,
            fileName: `stock-order__${stockOrderId}.json`,
          });
        })
        .catch((e) => {});
    },
    [loadingResourceData]
  );

  const [deleteStockOrderMutation, { loading: deletingStockOrder }] =
    useMutation<{
      deleteStockOrder: DeletionResponse;
    }>(MUTATION_DELETE_STOCK_ORDER);

  const [sendStockOrderInboundRetailer, { loading: sendingInBoundRetailer }] =
    useMutation<{
      sendStockOrderInboundRetailer: Success;
    }>(SEND_STOCK_ORDER_INBOUND_RETAILER);

  const flashMessagesContext = useFlashMessages();

  const handleOnDeleteFn = React.useCallback(() => {
    return deleteStockOrderMutation({
      variables: {
        id: stockOrder.id,
      },
    })
      .then((result) => {
        if (
          result.data?.deleteStockOrder.result &&
          result.data?.deleteStockOrder.result == DeletionResult.DELETED
        ) {
          setPromptVisible(false);
          navigation.goBack();
        } else {
          throw new Error(
            result.data?.deleteStockOrder.reason ?? "Something went wrong"
          );
        }
      })
      .catch((err: any) => {
        flashMessagesContext.showMessage({
          message: err.message ?? t("common:error"),
          severity: "error",
        });
      });
  }, []);

  const deletePrompt = (
    <ConfirmationPrompt
      visible={promptVisible}
      title={t("prompt.delete_order.title")}
      description={t("prompt.delete_order.description")}
      onCancelPress={() => setPromptVisible(false)}
      onConfirmPress={handleOnDeleteFn}
      disabled={deletingStockOrder}
    />
  );

  const handleOnSendInboundRetailer = React.useCallback(async () => {
    await sendStockOrderInboundRetailer({
      variables: {
        id: stockOrder.id,
      },
    })
      .then((result) => {
        if (result.data?.sendStockOrderInboundRetailer.success) {
          flashMessagesContext.showMessage({
            message: t("common:success"),
            severity: "success",
          });
        } else {
          throw new Error(t("common:error"));
        }
      })
      .catch((err: any) => {
        flashMessagesContext.showMessage({
          message: err.message ?? t("common:error"),
          severity: "error",
        });
      });
  }, [stockOrder.id]);

  let actions = [];

  if (stockOrder.status === "COMPLETE") {
    actions.push({
      isAction: true,
      id: "handleOnSendInboundRetailer",
      label: t("action.resend_retailer.label"),
      onPress: handleOnSendInboundRetailer,
      disabled: sendingInBoundRetailer,
      children: (
        <View style={{ alignItems: "center" }}>
          <Text
            color="subdued"
            textAlign="center"
            style={{ textAlign: "center" }}
          >
            {t("action.resend_retailer.description")}
          </Text>
        </View>
      ),
    });

    actions.push({
      isAction: true,
      id: "download_json",
      label: t("action.download_json.label"),
      onPress: () => handleGetFormattedResourceData(stockOrder.id),
      disabled: loadingResourceData,
      loading: loadingResourceData,
      children: (
        <View style={{ alignItems: "center" }}>
          <Text
            color="subdued"
            textAlign="center"
            style={{ textAlign: "center" }}
          >
            {t("action.download_json.description")}
          </Text>
        </View>
      ),
    });
  }

  if (stockOrder.status === "CANCELLED") {
    actions.push({
      isDestructive: true,
      id: "delete",
      label: t("action.delete_order.label"),
      onPress: () => setPromptVisible(true),
      disabled: false,
      children: (
        <View style={{ alignItems: "center" }}>
          <Text color="subdued" style={{ textAlign: "center" }}>
            {t("action.delete_order.description")}
          </Text>
        </View>
      ),
    });
  }

  const actionsMarkup =
    actions.length >= 1 ? (
      <View>
        <TileButtonActionList actions={actions} />
      </View>
    ) : null;

  const productMarkup = (
    <Card title={t("card.product.title")}>
      <Stack vertical>
        <OrderItemFlatActionList items={stockOrder.items} />
      </Stack>
    </Card>
  );

  const detailsMarkup = (
    <Card title={t("card.details.title")}>
      <FlatActionList
        items={[
          {
            key: "order_number",
            title: t("form.order_number.label"),
            subTitle: t("form.order_number.description"),
            componentRight: () => (
              <Text fontWeight="bold">{`#${stockOrder.formattedOrderNumber}`}</Text>
            ),
          },
          {
            key: "location",
            title: t("form.location.label"),
            subTitle: t("form.location.description"),
            componentRight: () => (
              <Text fontWeight="bold">{stockOrder.location?.name ?? "-"}</Text>
            ),
          },
          stockOrder.status === "CANCELLED"
            ? {
                key: "cancelled_at",
                title: t("form.cancelled_at.label"),
                subTitle: t("form.cancelled_at.description"),
                componentRight: () => (
                  <Text fontWeight="bold">
                    {stockOrder.updatedAt
                      ? `${formatDate(stockOrder.updatedAt, HTML5_FMT.DATE)}`
                      : "N/A"}
                  </Text>
                ),
              }
            : {
                key: "updated_at",
                title: t("form.completed_at.label"),
                subTitle: t("form.completed_at.description"),
                componentRight: () => (
                  <Text fontWeight="bold">
                    {stockOrder.completedAt
                      ? `${formatDate(stockOrder.completedAt, HTML5_FMT.DATE)}`
                      : "N/A"}
                  </Text>
                ),
              },
        ]}
      />
    </Card>
  );

  return (
    <Page
      title={t("view_title")}
      badges={
        <Stack spacing={Spacings.s2}>
          <Badge stockOrderStatus={stockOrder.status as any}></Badge>
        </Stack>
      }
    >
      <Stack vertical>
        {actionsMarkup}
        {detailsMarkup}
        {productMarkup}
      </Stack>
      {deletePrompt}
      <Spacer />
    </Page>
  );
};
